import { Flex, Heading } from '@everlywell/ui-kit';
import React, { memo, useCallback } from 'react';

import { type VideoSource } from '../../hooks/useEmbeddedVideo';
import lifeAtEverlyVideoMp4 from '../../videos/careers/life-at-everly.mp4';
import lifeAtEverlyVideoWebM from '../../videos/careers/life-at-everly.webm';
import EmbeddedVideo from '../EmbeddedVideo';
import plyrIconSprite from '../EmbeddedVideo/Sprites.svg';
import { themeBuilder } from '../EmbeddedVideo/theme';

export type CareersBannerProps = {
  title: string;
};

const plyrConfig = {
  controls: ['play'],
  autoplay: true,
  muted: true,
  iconUrl: plyrIconSprite,
  hideControls: false,
};

const lifeAtEverlySource: VideoSource = {
  type: 'video',
  sources: [
    {
      src: lifeAtEverlyVideoMp4,
      type: 'video/mp4',
    },
    {
      src: lifeAtEverlyVideoWebM,
      type: 'video/webm',
    },
  ],
};

const themeSx = themeBuilder({
  root: {
    borderRadius: '16px',
    overflow: 'hidden',
    '& video': {
      borderRadius: '16px',
      height: '100%',
      width: '100%',
      objectFit: 'cover',
    },
  },
  player: {
    base: {
      height: '100%',
    },
  },
  controls: {
    bar: {
      position: 'absolute',
      bottom: ['8px', null, '20px'],
      left: ['8px', null, '20px'],
      right: 'auto',
      width: 'auto',
      margin: 0,
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      background: 'none !important',
      transform: 'none',
    },
    barHover: {
      background: 'none !important',
    },
    control: {
      color: '#fff',
      transition: 'color 160ms ease, transform 160ms ease',
      '& svg rect': {
        fill: 'currentColor',
        transition: 'fill 160ms ease',
      },
      '& svg path': {
        fill: '#143927',
        transition: 'fill 160ms ease',
      },
    },
    controlHover: {
      background: 'none !important',
      color: '#EFF9F5',
      cursor: 'pointer',
    },
    controlIcon: {
      width: '32px',
      height: '32px',
    },
  },
});

const CareersBannerComponent = ({ title }: CareersBannerProps) => {
  const handlePlyrReady = useCallback((player: Plyr) => {
    const playPromise = player.play();
    if (playPromise?.catch) {
      playPromise.catch(() => undefined);
    }
  }, []);

  return (
    <>
      <Flex
        position="relative"
        marginTop={[48, null, 'calc(80px - 0.75em)']}
        alignItems="center"
        justifyContent="center"
      >
        <EmbeddedVideo
          kind="withSource"
          source={lifeAtEverlySource}
          options={plyrConfig}
          playsInline
          controls={false}
          autoPlay
          loop
          muted
          aria-hidden={true}
          onReady={handlePlyrReady}
          sx={themeSx}
        />
        <Heading
          as="h2"
          position="absolute"
          zIndex={1}
          alignItems="center"
          color="tints.lightGrey"
          fontWeight="410"
          fontFamily="'PP Agrandir', sans-serif"
          fontSize={['48px', '44px']}
          maxWidth={[160, '100%']}
          sx={{
            '@media (max-width: 1200px)': {
              fontSize: '36px',
            },
            '@media (max-width: 992px)': {
              fontSize: '32px',
            },
            '@media (max-width: 768px)': {
              fontSize: '28px',
            },
          }}
        >
          {title}
        </Heading>
      </Flex>
    </>
  );
};

const CareersBanner = memo(CareersBannerComponent);

export default CareersBanner;
